import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { connect, useSelector, shallowEqual } from "react-redux";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import * as auth from "../_redux/authRedux";
import { login } from "../_redux/authCrud";
import { getInviteByToken, acceptInvite, rejectInviteByToken } from "../../../UrlCrud";
import PasswordInput from "../../../../components/PasswordInput";

// Self-service "join an existing organisation" invite acceptance - reached
// via the link emailed from POST /v2/user/invite. Branches on whether the
// invited email already has an Anike account:
//   - No account yet: a short registration form (name + password) creates
//     one, pre-approved into the inviting company (still requires email
//     verification before login, same as any other account).
//   - Already has an account: must log in as that exact email, then
//     confirm, to add the company as an additional membership.
function AcceptInvite(props) {
    const token = props.match && props.match.params && props.match.params.token;

    const [invite, setInvite] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [success, setSuccess] = useState(null);

    const { isAuthorized, currentUser } = useSelector(
        ({ auth }) => ({
            isAuthorized: auth.user != null,
            currentUser: auth.user && auth.user.user,
        }),
        shallowEqual
    );

    useEffect(() => {
        if (!token) {
            setError("Missing invite token.");
            setLoading(false);
            return;
        }
        getInviteByToken(token)
            .then(({ data }) => setInvite(data))
            .catch(err => setError((err.response && err.response.data) || "This invite link is invalid or has expired."))
            .finally(() => setLoading(false));
    }, [token]);

    // --- Case: invited email already has an account ---
    const [loginValues, setLoginValues] = useState({ email: "", password: "" });
    const [loginSubmitting, setLoginSubmitting] = useState(false);
    const [loginError, setLoginError] = useState(null);

    const isLoggedInAsInvitee = isAuthorized && currentUser && invite
        && String(currentUser.email).toLowerCase() === String(invite.email).toLowerCase();

    const submitLogin = e => {
        e.preventDefault();
        setLoginSubmitting(true);
        setLoginError(null);
        login(loginValues.email, loginValues.password)
            .then(({ data: { accessToken } }) => {
                props.login(accessToken);
            })
            .catch(() => {
                setLoginSubmitting(false);
                setLoginError("Incorrect email or password.");
            });
    };

    const acceptAsExisting = () => {
        setLoading(true);
        setError(null);
        acceptInvite(token, {})
            .then(() => setSuccess({ text: `You've joined ${invite.companyName}.`, openApp: true }))
            .catch(err => setError((err.response && err.response.data) || "Failed to accept invite"))
            .finally(() => setLoading(false));
    };

    const decline = () => {
        if (!window.confirm(`Decline the invitation to join ${invite.companyName}?`)) return;
        setLoading(true);
        setError(null);
        rejectInviteByToken(token)
            .then(() => setSuccess({ text: `You declined the invitation to join ${invite.companyName}.` }))
            .catch(err => setError((err.response && err.response.data && (err.response.data.message || err.response.data)) || "Could not decline the invitation"))
            .finally(() => setLoading(false));
    };

    // --- Case: brand new signup ---
    const formik = useFormik({
        initialValues: { firstName: "", lastName: "", password: "", confirmPassword: "" },
        validationSchema: Yup.object().shape({
            firstName: Yup.string().required("First name is required"),
            lastName: Yup.string().required("Last name is required"),
            password: Yup.string().min(6, "Minimum 6 symbols").required("Password is required"),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref("password"), null], "Passwords must match")
                .required("Confirm your password"),
        }),
        onSubmit: (values, { setSubmitting }) => {
            setError(null);
            acceptInvite(token, values)
                .then(({ data }) => setSuccess({ text: data.message }))
                .catch(err => setError((err.response && err.response.data) || "Failed to accept invite"))
                .finally(() => setSubmitting(false));
        },
    });

    if (loading) {
        return <div className="login-form" style={{ display: "block" }}><div className="text-center text-muted">Loading invite...</div></div>;
    }

    if (error && !invite) {
        return (
            <div className="login-form" style={{ display: "block" }}>
                <div className="alert alert-custom alert-light-danger fade show mb-10" role="alert">
                    <div className="alert-text font-weight-bold">{typeof error === "string" ? error : JSON.stringify(error)}</div>
                </div>
                <Link to="/auth/login"><button type="button" className="btn btn-primary">Go to Login</button></Link>
            </div>
        );
    }

    if (success) {
        return (
            <div className="login-form" style={{ display: "block" }}>
                <div className="alert alert-custom alert-light-success fade show mb-10" role="alert">
                    <div className="alert-text font-weight-bold">{success.text}</div>
                </div>
                {success.openApp ? (
                    <button type="button" className="btn btn-primary" onClick={() => window.location.assign("/dashboard")}>Open Anike</button>
                ) : (
                    <Link to="/auth/login"><button type="button" className="btn btn-primary">Go to Login</button></Link>
                )}
            </div>
        );
    }

    return (
        <div className="login-form" style={{ display: "block" }}>
            <div className="text-center mb-10 mb-lg-20">
                <h3 className="font-size-h1">You're invited</h3>
                <p className="text-muted font-weight-bold">
                    {invite.inviterName || "Someone"} invited you to join <strong>{invite.companyName}</strong> on Anike.
                </p>
            </div>

            {error && (
                <div className="alert alert-custom alert-light-danger fade show mb-10" role="alert">
                    <div className="alert-text font-weight-bold">{typeof error === "string" ? error : JSON.stringify(error)}</div>
                </div>
            )}

            {invite.hasAccount ? (
                isLoggedInAsInvitee ? (
                    <div className="form-group d-flex flex-wrap flex-center">
                        <button type="button" className="btn btn-primary font-weight-bold px-9 py-4" onClick={acceptAsExisting}>
                            Accept and join {invite.companyName}
                        </button>
                    </div>
                ) : (
                    <>
                        <p className="text-muted mb-6">
                            An account already exists for <strong>{invite.email}</strong> - log in with that account to accept.
                        </p>
                        <form onSubmit={submitLogin} className="form">
                            {loginError && (
                                <div className="alert alert-custom alert-light-danger fade show mb-6">
                                    <div className="alert-text font-weight-bold">{loginError}</div>
                                </div>
                            )}
                            <div className="form-group">
                                <input
                                    type="email"
                                    className="form-control form-control-solid h-auto py-5 px-6"
                                    placeholder="Email"
                                    value={loginValues.email}
                                    onChange={e => setLoginValues({ ...loginValues, email: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <PasswordInput
                                    className="form-control form-control-solid h-auto py-5 px-6"
                                    placeholder="Password"
                                    value={loginValues.password}
                                    onChange={e => setLoginValues({ ...loginValues, password: e.target.value })}
                                />
                            </div>
                            <div className="form-group d-flex flex-wrap flex-center">
                                <button type="submit" className="btn btn-primary font-weight-bold px-9 py-4" disabled={loginSubmitting}>
                                    Log in and accept
                                </button>
                            </div>
                        </form>
                    </>
                )
            ) : (
                <form onSubmit={formik.handleSubmit} className="form">
                    <div className="form-group">
                        <input
                            placeholder="First name"
                            type="text"
                            className="form-control form-control-solid h-auto py-5 px-6"
                            name="firstName"
                            {...formik.getFieldProps("firstName")}
                        />
                        {formik.touched.firstName && formik.errors.firstName ? <div className="fv-help-block">{formik.errors.firstName}</div> : null}
                    </div>
                    <div className="form-group">
                        <input
                            placeholder="Last name"
                            type="text"
                            className="form-control form-control-solid h-auto py-5 px-6"
                            name="lastName"
                            {...formik.getFieldProps("lastName")}
                        />
                        {formik.touched.lastName && formik.errors.lastName ? <div className="fv-help-block">{formik.errors.lastName}</div> : null}
                    </div>
                    <div className="form-group">
                        <input
                            disabled
                            type="email"
                            className="form-control form-control-solid h-auto py-5 px-6"
                            value={invite.email}
                        />
                    </div>
                    <div className="form-group">
                        <PasswordInput
                            placeholder="Password"
                            className="form-control form-control-solid h-auto py-5 px-6"
                            name="password"
                            {...formik.getFieldProps("password")}
                        />
                        {formik.touched.password && formik.errors.password ? <div className="fv-help-block">{formik.errors.password}</div> : null}
                    </div>
                    <div className="form-group">
                        <PasswordInput
                            placeholder="Confirm password"
                            className="form-control form-control-solid h-auto py-5 px-6"
                            name="confirmPassword"
                            {...formik.getFieldProps("confirmPassword")}
                        />
                        {formik.touched.confirmPassword && formik.errors.confirmPassword ? <div className="fv-help-block">{formik.errors.confirmPassword}</div> : null}
                    </div>
                    <div className="form-group d-flex flex-wrap flex-center">
                        <button type="submit" className="btn btn-primary font-weight-bold px-9 py-4" disabled={formik.isSubmitting}>
                            Create account and join {invite.companyName}
                        </button>
                    </div>
                </form>
            )}

            <div className="text-center mt-8">
                <button type="button" className="btn btn-link text-muted" onClick={decline}>
                    No thanks - decline this invitation
                </button>
            </div>
        </div>
    );
}

export default connect(null, auth.actions)(AcceptInvite);
